import {client} from "./graphql/client";
import {formatApolloError} from "./graphql/error";
import {
    getActivityFeedQuery,
    getDepositActivityDetailQuery,
    getOnboardingActivityDetailQuery,
    getPaymentActivityDetailQuery,
    getRefundActivityDetailQuery,
    getSendReceivedActivityDetailQuery,
    getSentActivityDetailQuery,
    getWithdrawalActivityDetailQuery
} from "./graphql/activities/queries";
import {ACTIVITY_TYPES} from "../types";

export const activitiesDataProvider = {
    getList: async (resource, params) => {
        try {
            const {customerId, searchTerm, pagination, prevActivities, transactions} = params;
            const safeSearchTerm = searchTerm ? searchTerm : ""
            const result = await client.query({
                query: getActivityFeedQuery,
                variables: {
                    customerId: customerId,
                    searchTerm: safeSearchTerm,
                    pagination: pagination
                },
                fetchPolicy: 'network-only',
                notifyOnNetworkStatusChange: true
            });
            const feedItems = (result?.data?.getActivityFeedV2_Admin?.items ?? []);
            const basePrev = Array.isArray(prevActivities) ? prevActivities : [];
            const baseTxns = Array.isArray(transactions) ? transactions : [];

            const activities = feedItems.concat(basePrev).reduce((accumulator, current) => {
                if (!current) return accumulator;
                const exists = accumulator.find(item => item && (item.id === current.id));
                if (!exists) {
                    accumulator = accumulator.concat(current);
                }
                return accumulator;
            }, []);

            const enhancedActivities = activities.map(activity => {
                const transaction = baseTxns.find(transaction => {
                    return transaction && (transaction.orderId === activity?.reference || transaction.id === activity?.reference)
                }) || null;
                return {
                    ...activity,
                    linkedTransaction: transaction
                }
            });
            const total = enhancedActivities.length;
            return {
                data: enhancedActivities,
                total
            }
        } catch (e) {
            return Promise.reject({
                error: formatApolloError(e),
                data: [],
                total: 0
            });
        }

    },

    // TODO: implement when/if BFF has query to retrieve transaction by id
    getOne: async (resource, params) => {
        const {customerId, activityId, type, activityReference, isOtcDeposit} = params;
        let detailQuery
        let dataKey = ""
        let queryVariables = {customerId, activityId}
        
        switch (type) {
            case ACTIVITY_TYPES.OTC_ORDER:
                // For OTC orders, use activityReference as activityId - BFF has fallback to fetch from OTC API
                // Use different query based on whether it's a deposit or send
                if (isOtcDeposit) {
                    detailQuery = getDepositActivityDetailQuery
                    dataKey = 'getDepositActivityDetail_Admin'
                } else {
                    detailQuery = getSentActivityDetailQuery
                    dataKey = 'getSentActivityDetail_Admin'
                }
                queryVariables = {customerId, activityId: activityReference}
                break
            case ACTIVITY_TYPES.DEPOSIT:
                detailQuery = getDepositActivityDetailQuery
                dataKey = 'getDepositActivityDetail_Admin'
                break
            case ACTIVITY_TYPES.WITHDRAWAL:
                detailQuery = getWithdrawalActivityDetailQuery
                dataKey = 'getWithdrawalActivityDetail_Admin'
                break
            case ACTIVITY_TYPES.PAYMENT:
                detailQuery = getPaymentActivityDetailQuery
                dataKey = 'getPaymentActivityDetail_Admin'
                break
            case ACTIVITY_TYPES.REFUND:
                detailQuery = getRefundActivityDetailQuery
                dataKey = 'getRefundActivityDetail_Admin'
                break
            case ACTIVITY_TYPES.ONBOARDING:
                detailQuery = getOnboardingActivityDetailQuery
                dataKey = 'getOnboardingActivityDetail_Admin'
                break
            case ACTIVITY_TYPES.SENT:
                detailQuery = getSentActivityDetailQuery
                dataKey = 'getSentActivityDetail_Admin'
                break
            case ACTIVITY_TYPES.RECEIVED:
                detailQuery = getSendReceivedActivityDetailQuery
                dataKey = 'getSendReceivedActivityDetail_Admin'
                break
            default:
                detailQuery = null
                dataKey = ""
        }
        try {
            if (detailQuery) {
                const result = await client.query({
                    query: detailQuery,
                    variables: queryVariables,
                    fetchPolicy: 'network-only',
                    notifyOnNetworkStatusChange: true
                });
                const payload = result?.data?.[dataKey];
                if (!payload || typeof payload !== 'object') {
                    // Return a minimal record with id to satisfy RA's response format
                    return { data: { id: activityId } };
                }
                return {
                    data: {
                        id: activityId,
                        ...payload
                    }
                }
            } else throw "Cannot determine query for this activity"
        } catch (error) {
            return Promise.reject(error);
        }
    },
}